import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Sparkles } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { EXIT_INTENT_DELAY_MS } from "../lib/constants";
import ConsentCheckbox from "./ConsentCheckbox";

const STORAGE_KEY = "aimploy_exit_intent_shown";

// Pages where the popup may appear. Form pages (/kandidaten, /kontakt) are excluded
// so the overlay never covers a submit button while the user is actively filling.
const ALLOWED_PATHS = ["/", "/unternehmen"];

export default function ExitIntentPopup() {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [consent, setConsent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const location = useLocation();

    useEffect(() => {
        if (!ALLOWED_PATHS.includes(location.pathname)) return;
        if (sessionStorage.getItem(STORAGE_KEY)) return;

        let timer = setTimeout(() => {
            if (!sessionStorage.getItem(STORAGE_KEY)) {
                setOpen(true);
                sessionStorage.setItem(STORAGE_KEY, "1");
            }
        }, EXIT_INTENT_DELAY_MS);

        const onLeave = (e) => {
            if (e.clientY < 0 && !sessionStorage.getItem(STORAGE_KEY)) {
                setOpen(true);
                sessionStorage.setItem(STORAGE_KEY, "1");
            }
        };
        document.addEventListener("mouseleave", onLeave);
        return () => {
            clearTimeout(timer);
            document.removeEventListener("mouseleave", onLeave);
        };
    }, [location.pathname]);

    const submit = async (e) => {
        e.preventDefault();
        if (!consent) {
            toast.error("Bitte stimmen Sie der Datenschutzerklärung zu.");
            return;
        }
        setLoading(true);
        try {
            const res = await api.post("/leads", { name: name.trim(), email: email.trim(), source: "exit_intent" });
            setDone(true);
            if (res.data?.email_status === "pending_confirmation") {
                toast.success("Bestätigungs-Mail versendet — bitte Posteingang prüfen.");
            } else {
                toast.success("Vielen Dank! Wir melden uns gleich.");
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="exit-intent-popup"
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-aimploy-dark/70 backdrop-blur-sm fade-up"
        >
            <div className="relative max-w-lg w-full bg-white rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(26,26,46,0.4)]">
                <button
                    onClick={() => setOpen(false)}
                    className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-black/5"
                    aria-label="Schließen"
                    data-testid="exit-intent-close"
                >
                    <X className="w-5 h-5" />
                </button>
                <div className="bg-aimploy-navy text-white p-8 relative overflow-hidden">
                    <div className="absolute inset-0 bg-dots-dark opacity-40" />
                    <div className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-aimploy-violet/40 blur-2xl" />
                    <div className="relative">
                        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold mb-3">
                            <Sparkles className="w-3 h-3" /> Kostenlos
                        </span>
                        <h3 className="font-display text-3xl font-bold leading-tight mb-2">
                            Der Recruiting-Guide<br /> für 2026.
                        </h3>
                        <p className="text-sm text-white/70">
                            42 Seiten Insights aus 500+ Hires. Wie Sie Top-Talente finden — bevor es Ihre Wettbewerber tun.
                        </p>
                    </div>
                </div>
                <div className="p-8">
                    {done ? (
                        <div className="text-center py-6">
                            <div className="text-2xl font-display font-bold text-aimploy-navy mb-2">Check Ihre Inbox</div>
                            <p className="text-sm text-aimploy-muted">Der Guide ist auf dem Weg zu Ihnen.</p>
                            <button
                                onClick={() => setOpen(false)}
                                className="mt-6 bg-aimploy-navy text-white px-6 py-2.5 rounded-full text-sm font-semibold"
                            >
                                Schließen
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-3" data-testid="exit-intent-form">
                            <input
                                type="text"
                                placeholder="Ihr Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                data-testid="exit-intent-name"
                                className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5 text-sm"
                            />
                            <input
                                type="email"
                                placeholder="Geschäftliche E-Mail-Adresse"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="exit-intent-email"
                                className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5 text-sm"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="exit-intent-submit"
                                className="w-full bg-aimploy-violet hover:bg-aimploy-violet-hover text-white px-6 py-3.5 rounded-xl text-sm font-semibold disabled:opacity-60"
                            >
                                {loading ? "Wird gesendet..." : "Guide jetzt sichern"}
                            </button>
                            <ConsentCheckbox checked={consent} onChange={setConsent} testid="exit-intent-consent" />
                            <p className="text-xs text-aimploy-muted text-center">Kein Spam. Nur Substanz.</p>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
